"use client";
import ScreenLayout from "@/components/layout/screen-layout";
import { Trust_We_Won_Stats } from "@/lib/dto/customer-success/trust-we-won/trust-we-won-data";
import { useEffect, useRef, useState } from "react";

function StatsSectionTrustWeWon() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [direction, setDirection] = useState(1); // 1 for right-to-left, -1 for left-to-right
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial check
    checkIfMobile();

    // Add event listener for window resize
    window.addEventListener("resize", checkIfMobile);

    // Cleanup
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  useEffect(() => {
    setHasAnimated(true);
  }, []);

  const nextSlide = () => {
    setDirection(1); // Set direction to right-to-left
    setCurrentIndex((prevIndex) =>
      prevIndex === Trust_We_Won_Stats.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevSlide = () => {
    setDirection(-1); // Set direction to left-to-right
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? Trust_We_Won_Stats.length - 1 : prevIndex - 1
    );
  };

  // Handle touch events for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const touchDiff = touchStartX.current - touchEndX.current;

    // Minimum swipe distance (in px)
    if (Math.abs(touchDiff) > 50) {
      if (touchDiff > 0) {
        nextSlide(); // Swipe left
      } else {
        prevSlide(); // Swipe right
      }
    }
  };

  // Auto-slide for mobile
  useEffect(() => {
    if (!isMobile) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [isMobile]);

  return (
    <ScreenLayout outerClassName="bg-[#F9FAFD] border-y-1">
      <section className="py-6 lg:py-10">
        <div className="mx-auto">
          <h2 className="text-sm md:text-base font-semibold opacity-50 tracking-wide uppercase mb-4 text-center">
            THE TRUST WE WON OVER TIME
          </h2>

          {/* Mobile Carousel View */}
          <div
            className="md:hidden px-4 relative"
            ref={carouselRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="flex flex-col my-4 items-center justify-center overflow-hidden">
              {/* Content */}
              <div className="h-[70px] w-full flex items-center justify-center relative">
                <div
                  key={currentIndex}
                  className="flex items-center justify-center gap-x-4 absolute w-full"
                >
                  <div className="inline-flex items-baseline text-5xl text-custom-blue font-bold">
                    <span>
                      {Trust_We_Won_Stats[currentIndex].prefix}
                      {Trust_We_Won_Stats[currentIndex].value}
                      {Trust_We_Won_Stats[currentIndex].suffix}
                    </span>
                  </div>

                  <div className="text-sm font-semibold text-[#667085] uppercase max-w-[180px] leading-tight">
                    <div>
                      {Trust_We_Won_Stats[currentIndex].labelLines}
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress line indicator */}
              <div className="w-40 h-1 bg-gray-200 rounded-full overflow-hidden mt-4">
                <div
                  key={`progress-${currentIndex}`}
                  className="h-full bg-blue-600 rounded-full transition-all duration-1000 ease-linear"
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          </div>

          {/* Desktop View */}
          <div className="hidden md:flex md:flex-row w-full justify-center md:justify-around items-center gap-y-6 pt-2 px-4 md:px-0">
            {Trust_We_Won_Stats.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-center"
              >
                <div className="inline-flex items-baseline text-2xl md:text-3xl lg:text-[44px] xl:text-[53.16px] text-custom-blue font-semibold">
                  {item.prefix && <span className="pb-1.25">{item.prefix}</span>}
                  {item.value}
                  <span className="pl-1 pb-1.25">{item.suffix}</span>
                </div>

                <div className="text-sm md:text-[12px] lg:text-base font-semibold opacity-50 uppercase ml-4">
                  <p>{item.labelLines}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </ScreenLayout>
  );
}

export default StatsSectionTrustWeWon;
